import { useState, useMemo } from "react";
import { useQuery, useQueries, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import {
  Button,
  Badge,
  Skeleton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useToast,
} from "@asm/ui";
import {
  Plus,
  Radar,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  AlertTriangle,
  ChevronRight,
  Activity,
  ChevronLeft,
  ChevronsLeft,
  ChevronsRight,
  Trash2,
  ShieldAlert,
  ShieldCheck,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { scansApi, type Scan } from "@/lib/api";
import { simplifyResults } from "@/lib/simplify-results";
import { RiskBadge } from "@/components/insights";
import { formatModuleName } from "@/lib/utils";

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

// Status icon mapping
function StatusIcon({ status }: { status: Scan["status"] }) {
  switch (status) {
    case "completed":
      return <CheckCircle2 className="h-4 w-4 text-emerald-400" />;
    case "failed":
      return <XCircle className="h-4 w-4 text-red-400" />;
    case "running":
      return <Loader2 className="h-4 w-4 text-cyan-400 animate-spin" />;
    case "pending":
      return <Clock className="h-4 w-4 text-amber-400" />;
    case "cancelled":
      return <AlertTriangle className="h-4 w-4 text-muted-foreground" />;
    default:
      return null;
  }
}

// Scan row component - compact single-line layout
function ScanRow({ scan, onDelete, riskLevel }: { scan: Scan; onDelete: (scan: Scan) => void; riskLevel?: import("@/lib/api").RiskLevel }) {
  const { t: tCommon } = useTranslation("common");

  const statusStyles: Record<Scan["status"], string> = {
    pending: "border-amber-500/30 text-amber-400 bg-amber-500/10",
    running: "border-cyan-500/30 text-cyan-400 bg-cyan-500/10",
    completed: "border-emerald-500/30 text-emerald-400 bg-emerald-500/10",
    failed: "border-red-500/30 text-red-400 bg-red-500/10",
    cancelled: "border-muted text-muted-foreground bg-muted/50",
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onDelete(scan);
  };

  return (
    <Link
      to={`/scans/${scan.id}`}
      className={`
        group flex items-center gap-3 px-3 py-2 rounded-lg border border-border
        bg-card/50 hover:bg-card hover:border-cyan-500/30
        transition-all duration-200
      `}
    >
      {/* Status indicator */}
      <div className={`flex-shrink-0 p-1.5 rounded-md border ${statusStyles[scan.status]}`}>
        <StatusIcon status={scan.status} />
      </div>

      {/* Target */}
      <div className="flex-1 min-w-0 flex items-center gap-3">
        <h3 className="font-mono text-sm font-medium text-foreground truncate">
          {scan.target}
        </h3>
        {scan.status === "running" && (
          <Activity className="h-3 w-3 text-cyan-400 animate-pulse flex-shrink-0" />
        )}
      </div>

      {/* Modules - inline */}
      <div className="hidden md:flex items-center gap-1 flex-shrink-0">
        {scan.modules.slice(0, 3).map((module) => (
          <span
            key={module}
            className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-muted text-muted-foreground tracking-wider"
          >
            {formatModuleName(module)}
          </span>
        ))}
        {scan.modules.length > 3 && (
          <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-muted text-muted-foreground">
            +{scan.modules.length - 3}
          </span>
        )}
      </div>

      {/* Date */}
      <span className="hidden sm:block text-xs text-muted-foreground font-mono flex-shrink-0">
        {new Date(scan.created_at).toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
        })}
      </span>

      {/* Risk level badge for completed scans */}
      {riskLevel && (
        <div className="flex-shrink-0">
          <RiskBadge level={riskLevel} size="sm" />
        </div>
      )}

      {/* Status badge */}
      <Badge variant="outline" className={`flex-shrink-0 ${statusStyles[scan.status]}`}>
        {tCommon(`status.${scan.status}`)}
      </Badge>

      {/* Delete button */}
      <button
        onClick={handleDeleteClick}
        className="flex-shrink-0 p-1.5 rounded-md text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors opacity-0 group-hover:opacity-100"
        title="Delete scan"
      >
        <Trash2 className="h-4 w-4" />
      </button>

      {/* Arrow */}
      <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-cyan-400 transition-colors flex-shrink-0" />
    </Link>
  );
}

// Loading skeleton - compact
function ScanSkeleton() {
  return (
    <div className="flex items-center gap-3 px-3 py-2 rounded-lg border border-border bg-card/50">
      <Skeleton className="h-7 w-7 rounded-md" />
      <Skeleton className="h-4 w-32 flex-1" />
      <Skeleton className="hidden md:block h-4 w-24" />
      <Skeleton className="h-5 w-16" />
    </div>
  );
}

// Empty state
function EmptyState() {
  const { t } = useTranslation("scans");

  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
      <div className="p-4 rounded-xl bg-muted/30 border border-border mb-6">
        <Radar className="h-12 w-12 text-muted-foreground" />
      </div>
      <h3 className="text-lg font-semibold mb-2">No scans yet</h3>
      <p className="text-muted-foreground text-sm max-w-sm mb-6">
        Launch your first security reconnaissance scan to start discovering your attack surface.
      </p>
      <Button asChild className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold">
        <Link to="/scans/new">
          <Plus className="mr-2 h-4 w-4" />
          {t("list.startFirst", "Start your first scan")}
        </Link>
      </Button>
    </div>
  );
}

// Pagination component
function Pagination({
  page,
  perPage,
  total,
  onPageChange,
  onPerPageChange,
}: {
  page: number;
  perPage: number;
  total: number;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
}) {
  const { t } = useTranslation("scans");
  const totalPages = Math.ceil(total / perPage);
  const startItem = (page - 1) * perPage + 1;
  const endItem = Math.min(page * perPage, total);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | "ellipsis")[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible + 2) {
      // Show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (page > 3) {
        pages.push("ellipsis");
      }

      // Show pages around current
      const start = Math.max(2, page - 1);
      const end = Math.min(totalPages - 1, page + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (page < totalPages - 2) {
        pages.push("ellipsis");
      }

      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  if (total === 0) return null;

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-border">
      {/* Left side: showing X of Y */}
      <div className="text-sm text-muted-foreground">
        {t("pagination.showing", "Showing")} <span className="font-mono text-foreground">{startItem}</span>-<span className="font-mono text-foreground">{endItem}</span> {t("pagination.of", "of")} <span className="font-mono text-foreground">{total}</span> {t("pagination.results", "results")}
      </div>

      {/* Center: Page numbers */}
      <div className="flex items-center gap-1">
        {/* First page */}
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => onPageChange(1)}
          disabled={page === 1}
        >
          <ChevronsLeft className="h-4 w-4" />
        </Button>

        {/* Previous page */}
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        {/* Page numbers */}
        {getPageNumbers().map((pageNum, idx) =>
          pageNum === "ellipsis" ? (
            <span key={`ellipsis-${idx}`} className="px-2 text-muted-foreground">
              ...
            </span>
          ) : (
            <Button
              key={pageNum}
              variant={page === pageNum ? "default" : "ghost"}
              size="icon"
              className={`h-8 w-8 font-mono text-sm ${
                page === pageNum
                  ? "bg-cyan-500 hover:bg-cyan-400 text-black"
                  : ""
              }`}
              onClick={() => onPageChange(pageNum)}
            >
              {pageNum}
            </Button>
          )
        )}

        {/* Next page */}
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => onPageChange(page + 1)}
          disabled={page === totalPages}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>

        {/* Last page */}
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => onPageChange(totalPages)}
          disabled={page === totalPages}
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Right side: Per page selector */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">{t("pagination.perPage", "Per page")}</span>
        <Select
          value={perPage.toString()}
          onValueChange={(value) => onPerPageChange(parseInt(value))}
        >
          <SelectTrigger className="w-20 h-8 bg-muted/50 border-border font-mono">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PER_PAGE_OPTIONS.map((option) => (
              <SelectItem key={option} value={option.toString()}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

export default function Scans() {
  const { t } = useTranslation("scans");
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [scanToDelete, setScanToDelete] = useState<Scan | null>(null);

  // Get pagination from URL or defaults
  const page = parseInt(searchParams.get("page") || "1");
  const perPage = parseInt(searchParams.get("per_page") || "10");

  // Fetch paginated scans
  const { data: paginatedData, isLoading } = useQuery({
    queryKey: ["scans", page, perPage],
    queryFn: () => scansApi.list({ page, per_page: perPage }),
  });

  const scans = paginatedData?.scans || [];
  const total = paginatedData?.total || 0;

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => scansApi.delete(id),
    onSuccess: () => {
      // If this was the last item on the current page and we're not on page 1,
      // go back to the previous page
      if (scans.length === 1 && page > 1) {
        setSearchParams((prev) => {
          prev.set("page", (page - 1).toString());
          return prev;
        });
      }
      queryClient.invalidateQueries({ queryKey: ["scans"] });
      setDeleteDialogOpen(false);
      toast({
        title: t("delete.success", "Scan deleted"),
        description: t("delete.successDescription", "The scan and all associated data have been removed."),
        variant: "success",
      });
      setScanToDelete(null);
    },
    onError: (error: Error) => {
      toast({
        title: t("delete.error", "Failed to delete scan"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Update URL when pagination changes
  const handlePageChange = (newPage: number) => {
    setSearchParams((prev) => {
      prev.set("page", newPage.toString());
      return prev;
    });
  };

  const handlePerPageChange = (newPerPage: number) => {
    setSearchParams((prev) => {
      prev.set("per_page", newPerPage.toString());
      prev.set("page", "1"); // Reset to first page when changing per_page
      return prev;
    });
  };

  const handleDeleteClick = (scan: Scan) => {
    setScanToDelete(scan);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (scanToDelete) {
      deleteMutation.mutate(scanToDelete.id);
    }
  };

  // Fetch results for completed scans on current page to extract risk levels
  const completedScans = scans.filter((s) => s.status === "completed");
  const scanResultQueries = useQueries({
    queries: completedScans.map((scan) => ({
      queryKey: ["scanResults", scan.id],
      queryFn: () => scansApi.getResults(scan.id),
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes — results don't change
      enabled: scan.status === "completed",
    })),
  });

  // Build a map of scanId → riskLevel from fetched results
  const riskByScandId = useMemo(() => {
    const map: Record<string, import("@/lib/api").RiskLevel> = {};
    completedScans.forEach((scan, i) => {
      const query = scanResultQueries[i];
      if (query?.data) {
        try {
          const simplified = simplifyResults(query.data);
          map[scan.id] = simplified.score.riskLevel;
        } catch {
          // If simplifyResults fails, skip this scan
        }
      }
    });
    return map;
  }, [completedScans, scanResultQueries]);

  // Security-focused stats derived from risk analysis
  const stats = useMemo(() => {
    const running = scans.filter((s) => s.status === "running").length;
    let criticalIssues = 0;
    let withIssues = 0;
    let clean = 0;

    for (const scan of scans) {
      const risk = riskByScandId[scan.id];
      if (!risk) continue; // Not completed or not loaded yet
      if (risk === "critical" || risk === "high") criticalIssues++;
      else if (risk === "medium") withIssues++;
      else clean++; // low or info
    }

    return { criticalIssues, withIssues, clean, running };
  }, [scans, riskByScandId]);

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
              <Radar className="h-8 w-8 text-cyan-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">{t("title", "Scans")}</h1>
              <p className="text-muted-foreground">{t("description", "View and manage your security scans")}</p>
            </div>
          </div>

          <Button asChild className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold">
            <Link to="/scans/new">
              <Plus className="mr-2 h-4 w-4" />
              {t("newScan", "New Scan")}
            </Link>
          </Button>
        </div>
      </div>

      {/* Stats bar — security-focused metrics */}
      {!isLoading && total > 0 && (
        <div className="grid grid-cols-4 gap-4 mb-8">
          <div className="rounded-lg border border-red-500/20 bg-red-500/5 p-4">
            <div className="flex items-center gap-2 mb-1">
              <ShieldAlert className="h-3.5 w-3.5 text-red-400" />
              <p className="text-xs text-red-400 uppercase tracking-wider">Critical / High</p>
            </div>
            <p className="text-2xl font-bold font-mono text-red-400">{stats.criticalIssues}</p>
          </div>
          <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-4">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-400" />
              <p className="text-xs text-amber-400 uppercase tracking-wider">Issues Found</p>
            </div>
            <p className="text-2xl font-bold font-mono text-amber-400">{stats.withIssues}</p>
          </div>
          <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-4">
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />
              <p className="text-xs text-emerald-400 uppercase tracking-wider">Clean</p>
            </div>
            <p className="text-2xl font-bold font-mono text-emerald-400">{stats.clean}</p>
          </div>
          <div className="rounded-lg border border-cyan-500/20 bg-cyan-500/5 p-4">
            <div className="flex items-center gap-2 mb-1">
              <Loader2 className="h-3.5 w-3.5 text-cyan-400" />
              <p className="text-xs text-cyan-400 uppercase tracking-wider">In Progress</p>
            </div>
            <p className="text-2xl font-bold font-mono text-cyan-400">{stats.running}</p>
          </div>
        </div>
      )}

      {/* Scan list */}
      <div className="rounded-lg border border-border bg-card/30 p-6">
        <div className="flex items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="h-6 w-1 rounded-full bg-cyan-500" />
            <h2 className="text-lg font-semibold">{t("list.title", "All Scans")}</h2>
          </div>
          {total > 0 && (
            <span className="text-sm text-muted-foreground">
              {t("pagination.page", "Page")} <span className="font-mono text-foreground">{page}</span> {t("pagination.of", "of")} <span className="font-mono text-foreground">{Math.ceil(total / perPage)}</span>
            </span>
          )}
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {[...Array(perPage > 5 ? 5 : perPage)].map((_, i) => (
              <ScanSkeleton key={i} />
            ))}
          </div>
        ) : scans.length ? (
          <>
            <div className="space-y-3">
              {scans.map((scan) => (
                <ScanRow key={scan.id} scan={scan} onDelete={handleDeleteClick} riskLevel={riskByScandId[scan.id]} />
              ))}
            </div>

            {/* Pagination */}
            <Pagination
              page={page}
              perPage={perPage}
              total={total}
              onPageChange={handlePageChange}
              onPerPageChange={handlePerPageChange}
            />
          </>
        ) : (
          <EmptyState />
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-card border-red-500/30 max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg bg-red-500/10 border border-red-500/20">
                <AlertTriangle className="h-5 w-5 text-red-400" />
              </div>
              <DialogTitle className="text-lg font-semibold">
                {t("delete.title", "Delete Scan")}
              </DialogTitle>
            </div>
            <DialogDescription className="text-muted-foreground">
              {t("delete.description", "This action cannot be undone. This will permanently delete the scan and all associated results from the system.")}
            </DialogDescription>
          </DialogHeader>

          {scanToDelete && (
            <div className="mt-4 p-3 rounded-lg bg-muted/30 border border-border">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                {t("delete.target", "Target")}
              </p>
              <p className="font-mono text-sm text-foreground">{scanToDelete.target}</p>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-xs text-muted-foreground">
                  {scanToDelete.modules.length} {t("delete.modules", "modules")}
                </span>
                <span className="text-muted-foreground">•</span>
                <span className="text-xs text-muted-foreground">
                  {new Date(scanToDelete.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          )}

          <DialogFooter className="mt-6 gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleteMutation.isPending}
            >
              {t("delete.cancel", "Cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={deleteMutation.isPending}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("delete.deleting", "Deleting...")}
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t("delete.confirm", "Delete Scan")}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
